import { MediaService } from '@shared/services/media.service';
import { MediaFile, MediaType } from '@shared/types/media.types';
import api from '../../../shared/services/api';
import { TestDetail } from '../types/test-detail.types';

export class TestEditorService {
  private static readonly BASE_URL = '/tests';

  /**
   * Get test for editing
   */
  static async getTestForEditing(testId: string): Promise<TestDetail> {
    try {
      const response = await api.get(`${this.BASE_URL}/${testId}/edit`);

      // Validate response structure
      if (
        !response.data ||
        !response.data.id ||
        !Array.isArray(response.data.sections)
      ) {
        throw new Error('Invalid test data received from server');
      }

      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('Test not found');
      }
      if (error.response?.status === 401) {
        throw new Error('Unauthorized access');
      }
      throw new Error(error.message || 'Error loading test');
    }
  }

  /**
   * Save test changes
   */
  static async saveTest(
    testId: string,
    testData: Partial<TestDetail>
  ): Promise<TestDetail> {
    try {
      const response = await api.put(`${this.BASE_URL}/${testId}`, testData);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 401) {
        throw new Error('Unauthorized access');
      }
      throw new Error(error.message || 'Error saving test');
    }
  }

  /**
   * Check if test name exists
   */
  static async checkTestNameExists(
    userId: string,
    name: string,
    excludeTestId?: string
  ): Promise<boolean> {
    try {
      const response = await api.get(`${this.BASE_URL}/check-name`, {
        params: { userId, name, excludeTestId },
      });
      return response.data.exists;
    } catch (error: any) {
      throw new Error(error.message || 'Error checking test name');
    }
  }

  /**
   * Upload image and get URL
   */
  static async uploadImage(userId: string, file: File): Promise<string> {
    try {
      const mediaFile: MediaFile = {
        file,
        preview: URL.createObjectURL(file),
        type: MediaType.IMAGE,
        progress: 0,
      };

      const imageUrl = await MediaService.uploadMedia(userId, mediaFile, {
        maxSize: 5 * 1024 * 1024, // 5MB
        acceptedTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
      });

      return imageUrl;
    } catch (error: any) {
      throw new Error(error.message || 'Error uploading image');
    }
  }

  /**
   * Merge sections
   */
  static async mergeSections(
    testId: string,
    sectionId1: string,
    sectionId2: string
  ): Promise<TestDetail> {
    try {
      const response = await api.post(
        `${this.BASE_URL}/${testId}/merge-sections`,
        {
          sectionId1,
          sectionId2,
        }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.message || 'Error merging sections');
    }
  }
}
